import { useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import {
  fetchSubcontractorPortalData,
  savePhaseStartDate,
  submitTimelinePhasePhoto,
  type SubcontractorPortalData,
  type PortalBeneficiary,
} from "../api/subcontractorPortalApi";

// Compress image to base64 (max 1024px, 70% quality)
function compressImageToBase64(file: File, maxWidth = 1024, maxHeight = 1024, quality = 0.7): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = (event) => {
      const img = new Image();
      img.src = event.target?.result as string;
      img.onload = () => {
        let width = img.width;
        let height = img.height;
        if (width > maxWidth || height > maxHeight) {
          if (width > height) { height = Math.round((height * maxWidth) / width); width = maxWidth; }
          else { width = Math.round((width * maxHeight) / height); height = maxHeight; }
        }
        const canvas = document.createElement("canvas");
        canvas.width = width; canvas.height = height;
        const ctx = canvas.getContext("2d");
        if (!ctx) return reject(new Error("Could not get canvas context"));
        ctx.drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL("image/jpeg", quality));
      };
      img.onerror = (error) => reject(error);
    };
    reader.onerror = (error) => reject(error);
  });
}

function readVideoAsBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = (e) => reject(e);
  });
}

const MAX_VIDEO_BYTES = 25 * 1024 * 1024; // 25 MB

function getDistanceMeters(lat1: number, lon1: number, lat2: number, lon2: number) {
  const R = 6371e3; // metres
  const phi1 = lat1 * Math.PI/180;
  const phi2 = lat2 * Math.PI/180;
  const deltaPhi = (lat2-lat1) * Math.PI/180;
  const deltaLambda = (lon2-lon1) * Math.PI/180;

  const a = Math.sin(deltaPhi/2) * Math.sin(deltaPhi/2) +
            Math.cos(phi1) * Math.cos(phi2) *
            Math.sin(deltaLambda/2) * Math.sin(deltaLambda/2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  return R * c;
}

function StatusBadge({ status }: { status: string }) {
  let color = "#6b7280";
  let bg = "#f3f4f6";
  if (status === "Approved") { color = "#15803d"; bg = "#dcfce7"; }
  else if (status === "Submitted") { color = "#0369a1"; bg = "#e0f2fe"; }
  else if (status === "Rejected") { color = "#b91c1c"; bg = "#fee2e2"; }
  else if (status === "Hold") { color = "#b45309"; bg = "#fef3c7"; }
  return (
    <span style={{ fontSize: "0.75rem", fontWeight: 600, padding: "0.2rem 0.5rem", borderRadius: "9999px", backgroundColor: bg, color }}>
      {status}
    </span>
  );
}

function PhotoSlot({
  label, required, preview, inputId, inputRef, onSelect, onClear,
}: {
  label: string; required: boolean; preview: string | null;
  inputId: string; inputRef: React.RefObject<HTMLInputElement>;
  onSelect: (file: File) => void; onClear: () => void;
}) {
  return (
    <div style={{ flex: "1 1 0", minWidth: 0 }}>
      <p style={{ fontSize: "0.78rem", fontWeight: 600, marginBottom: "0.4rem", color: "#374151" }}>
        {label} {required ? <span style={{ color: "#b91c1c" }}>*</span> : <span style={{ color: "#6b7280", fontWeight: 400 }}>(opt.)</span>}
      </p>
      {!preview ? (
        <div style={{ position: "relative" }}>
          <input
            type="file" accept="image/*"
            id={inputId} ref={inputRef}
            onChange={(e) => { const f = e.target.files?.[0]; if (f) onSelect(f); }}
            style={{ opacity: 0, position: "absolute", inset: 0, width: "100%", height: "100%", cursor: "pointer" }}
          />
          <label htmlFor={inputId} style={{
            display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
            border: "2px dashed #d1d5db", borderRadius: "0.5rem", padding: "1rem 0.25rem",
            cursor: "pointer", background: "#f9fafb", color: "#6b7280", fontSize: "0.75rem",
            pointerEvents: "none",
          }}>
            <span style={{ fontSize: "1.4rem", marginBottom: "0.2rem" }}>📷</span>
            Add photo
          </label>
        </div>
      ) : (
        <div>
          <img src={preview} alt={label} style={{
            width: "100%", aspectRatio: "4/3", objectFit: "cover",
            borderRadius: "0.375rem", border: "1px solid #d1d5db", display: "block",
          }} />
          <button type="button" onClick={onClear} style={{
            marginTop: "0.3rem", width: "100%", fontSize: "0.72rem", padding: "0.3rem",
            background: "#fee2e2", color: "#b91c1c", border: "none", borderRadius: "0.25rem", cursor: "pointer",
          }}>
            Retake
          </button>
        </div>
      )}
    </div>
  );
}

export function SubcontractorPortalPage() {
  const { token } = useParams<{ token: string }>();
  const [data, setData] = useState<SubcontractorPortalData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedBen, setSelectedBen] = useState<PortalBeneficiary | null>(null);

  // Start date step
  const [startDateInput, setStartDateInput] = useState("");
  const [savingStartDate, setSavingStartDate] = useState(false);

  // Photo + video step (only shown after start date saved)
  const [photo1File, setPhoto1File] = useState<File | null>(null);
  const [photo2File, setPhoto2File] = useState<File | null>(null);
  const [photo3File, setPhoto3File] = useState<File | null>(null);
  const [photo1Preview, setPhoto1Preview] = useState<string | null>(null);
  const [photo2Preview, setPhoto2Preview] = useState<string | null>(null);
  const [photo3Preview, setPhoto3Preview] = useState<string | null>(null);
  const photo1Ref = useRef<HTMLInputElement>(null);
  const photo2Ref = useRef<HTMLInputElement>(null);
  const photo3Ref = useRef<HTMLInputElement>(null);

  const [videoFile, setVideoFile] = useState<File | null>(null);
  const [videoPreviewUrl, setVideoPreviewUrl] = useState<string | null>(null);
  const [videoError, setVideoError] = useState<string | null>(null);
  const videoInputRef = useRef<HTMLInputElement>(null);

  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!token) { setError("Invalid access link."); setLoading(false); return; }
    fetchSubcontractorPortalData(token)
      .then((d) => { setData(d); setLoading(false); })
      .catch((e) => { setError(e instanceof Error ? e.message : "Failed to load portal data"); setLoading(false); });
  }, [token]);

  const clearPhotoState = () => {
    setPhoto1File(null); setPhoto1Preview(null); if (photo1Ref.current) photo1Ref.current.value = "";
    setPhoto2File(null); setPhoto2Preview(null); if (photo2Ref.current) photo2Ref.current.value = "";
    setPhoto3File(null); setPhoto3Preview(null); if (photo3Ref.current) photo3Ref.current.value = "";
    setVideoFile(null); setVideoPreviewUrl(null); setVideoError(null);
    if (videoInputRef.current) videoInputRef.current.value = "";
  };

  const clearAll = () => {
    setStartDateInput("");
    clearPhotoState();
  };

  const refreshBen = async (applicationId: string) => {
    if (!token) return null;
    const newData = await fetchSubcontractorPortalData(token);
    setData(newData);
    return newData.beneficiaries.find((b) => b.applicationId === applicationId) ?? null;
  };

  // Step 1: Save start date (immutable once saved)
  const handleSaveStartDate = async () => {
    if (!token || !selectedBen || !selectedBen.activePhaseId || !startDateInput) return;
    setSavingStartDate(true);
    try {
      await savePhaseStartDate(token, selectedBen.applicationId, selectedBen.activePhaseId, startDateInput);
      const updated = await refreshBen(selectedBen.applicationId);
      setSelectedBen(updated);
    } catch (err) {
      alert(err instanceof Error ? err.message : "Failed to save start date");
    } finally {
      setSavingStartDate(false);
    }
  };

  // Step 2: Submit photos + video
  const handleSubmitPhase = async () => {
    if (!token || !selectedBen || !selectedBen.activePhaseId) return;
    if (!photo1File || !photo2File || !photo3File) { alert("Please add all 3 required photos."); return; }
    
    setSubmitting(true);
    
    // Geofencing validation
    try {
      const position = await new Promise<GeolocationPosition>((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, { enableHighAccuracy: true, timeout: 15000 });
      });
      
      if (selectedBen.plotLatitude && selectedBen.plotLongitude) {
        const dist = getDistanceMeters(
          position.coords.latitude, position.coords.longitude,
          selectedBen.plotLatitude, selectedBen.plotLongitude
        );
        
        if (dist > 50) {
          alert(`You are ${Math.round(dist)}m away from the plot location. You must be within 50m to submit progress photos.`);
          setSubmitting(false);
          return;
        }
      }
    } catch (err) {
      setSubmitting(false);
      alert("Failed to get your location. Geotagging is required to upload progress.");
      return;
    }

    try {
      const [b1, b2, b3] = await Promise.all([
        compressImageToBase64(photo1File),
        compressImageToBase64(photo2File),
        compressImageToBase64(photo3File),
      ]);
      let videoBase64: string | undefined;
      if (videoFile) videoBase64 = await readVideoAsBase64(videoFile);

      await submitTimelinePhasePhoto(token, selectedBen.applicationId, selectedBen.activePhaseId, b1, {
        photoData2: b2,
        photoData3: b3,
        videoData: videoBase64,
      });

      const updated = await refreshBen(selectedBen.applicationId);
      setSelectedBen(updated);
      clearPhotoState();
      alert("Progress submitted successfully!");
    } catch (err) {
      alert(err instanceof Error ? err.message : "Failed to submit progress");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh", backgroundColor: "#f3f4f6" }}>
        <p style={{ display: "flex", alignItems: "center", gap: "0.5rem", fontSize: "1.2rem", color: "#4b5563" }}>
          <span className="spinner spinner--md" /> Loading…
        </p>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh", padding: "1rem", backgroundColor: "#f3f4f6" }}>
        <div className="card card--elevated" style={{ textAlign: "center", maxWidth: "400px" }}>
          <h2 style={{ color: "#b91c1c", marginBottom: "1rem" }}>Access Error</h2>
          <p>{error || "Unable to load data."}</p>
          <p style={{ marginTop: "1rem", fontSize: "0.85rem", color: "#6b7280" }}>Please request a new link from the Housing Office.</p>
        </div>
      </div>
    );
  }

  // ── Beneficiary Detail View ──────────────────────────────────────────────
  if (selectedBen) {
    return (
      <div style={{ height: "100vh", overflowY: "auto", backgroundColor: "#f3f4f6", display: "flex", flexDirection: "column" }}>
        <header style={{ backgroundColor: "var(--corp-blue)", color: "white", padding: "1rem", position: "sticky", top: 0, zIndex: 10 }}>
          <button
            className="btn btn--link"
            style={{ color: "white", padding: 0, marginBottom: "0.5rem", display: "flex", alignItems: "center", gap: "0.25rem" }}
            onClick={() => { setSelectedBen(null); clearAll(); }}
          >
            ← Back to list
          </button>
          <h1 style={{ fontSize: "1.25rem", margin: 0 }}>{selectedBen.beneficiaryName}</h1>
          <p style={{ margin: 0, opacity: 0.9, fontSize: "0.85rem" }}>{selectedBen.village}, {selectedBen.mandal}</p>
        </header>

        <main style={{ padding: "1rem", paddingBottom: "6rem", flex: 1 }}>
          <h2 className="form-section-title" style={{ marginTop: 0 }}>Construction Timeline</h2>

          <div style={{ display: "flex", flexDirection: "column", gap: "1rem", position: "relative" }}>
            <div style={{ position: "absolute", left: "1.25rem", top: "1rem", bottom: "1rem", width: "2px", backgroundColor: "#e5e7eb", zIndex: 0 }} />

            {selectedBen.timeline.map((phase) => {
              const isActive = phase.id === selectedBen.activePhaseId;
              const isPast = ["Approved", "Submitted", "Hold"].includes(phase.status);
              const isLocked = !isActive && !isPast;

              let iconBg = "#e5e7eb"; let iconColor = "#9ca3af";
              if (phase.status === "Approved") { iconBg = "#15803d"; iconColor = "white"; }
              else if (phase.status === "Submitted") { iconBg = "#0369a1"; iconColor = "white"; }
              else if (isActive) { iconBg = "var(--corp-blue)"; iconColor = "white"; }

              // The saved start date for this phase
              const savedStartDate = phase.actualStartDate;
              const startDateLocked = Boolean(savedStartDate);

              return (
                <div key={phase.id} style={{ display: "flex", gap: "1rem", position: "relative", zIndex: 1, opacity: isLocked ? 0.55 : 1 }}>
                  <div style={{
                    width: "2.5rem", height: "2.5rem", borderRadius: "50%",
                    backgroundColor: iconBg, color: iconColor,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontWeight: "bold", flexShrink: 0, border: "2px solid white",
                    boxShadow: "0 1px 2px rgba(0,0,0,0.1)",
                  }}>
                    {phase.status === "Approved" ? "✓" : phase.sortOrder}
                  </div>

                  <div className="card card--flush" style={{ flex: 1, padding: "1rem" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "0.5rem" }}>
                      <h3 style={{ margin: 0, fontSize: "1.05rem" }}>{phase.title}</h3>
                      <StatusBadge status={phase.status} />
                    </div>

                    {phase.submittedAt && (
                      <p style={{ margin: "0 0 0.25rem", fontSize: "0.8rem", color: "#6b7280" }}>
                        Submitted: {new Date(phase.submittedAt).toLocaleDateString()}
                      </p>
                    )}

                    {/* Show saved start date as read-only on all non-pending phases */}
                    {savedStartDate && (
                      <p style={{ margin: "0 0 0.25rem", fontSize: "0.8rem", color: "#374151" }}>
                        <span style={{ fontWeight: 600 }}>Work started:</span> {savedStartDate}
                      </p>
                    )}

                    {/* ── Active phase actions ── */}
                    {isActive && (
                      <div style={{ marginTop: "1rem", paddingTop: "1rem", borderTop: "1px solid #e5e7eb" }}>

                        {/* ── STEP 1: Actual Start Date ── */}
                        <div style={{
                          marginBottom: "1.25rem", padding: "0.85rem",
                          background: startDateLocked ? "#f0fdf4" : "#fffbeb",
                          borderRadius: "0.5rem",
                          border: `1px solid ${startDateLocked ? "#86efac" : "#fcd34d"}`,
                        }}>
                          <p style={{ fontSize: "0.82rem", fontWeight: 700, marginBottom: "0.5rem", color: startDateLocked ? "#15803d" : "#92400e" }}>
                            {startDateLocked ? "✅ Step 1 — Work start date saved" : "Step 1 — Enter actual start date"}
                          </p>

                          {startDateLocked ? (
                            /* Read-only locked display */
                            <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                              <span style={{ fontSize: "1rem", fontWeight: 700, color: "#15803d" }}>
                                📅 {savedStartDate}
                              </span>
                              <span style={{ fontSize: "0.75rem", color: "#6b7280" }}>(cannot be changed)</span>
                            </div>
                          ) : (
                            /* Editable date input + Save button */
                            <>
                              <p style={{ fontSize: "0.78rem", color: "#78350f", marginBottom: "0.5rem" }}>
                                Enter the date when work on this phase actually started. Once saved it cannot be edited.
                              </p>
                              <div style={{ display: "flex", gap: "0.5rem", alignItems: "flex-end", flexWrap: "wrap" }}>
                                <div style={{ flex: 1, minWidth: "10rem" }}>
                                  <input
                                    type="date"
                                    className="form-control"
                                    value={startDateInput}
                                    max={new Date().toISOString().slice(0, 10)}
                                    onChange={(e) => setStartDateInput(e.target.value)}
                                    style={{ width: "100%" }}
                                  />
                                </div>
                                <button
                                  type="button"
                                  className="btn btn--primary btn--tg"
                                  disabled={!startDateInput || savingStartDate}
                                  onClick={() => void handleSaveStartDate()}
                                  style={{ whiteSpace: "nowrap" }}
                                >
                                  {savingStartDate ? "Saving…" : "Save Start Date"}
                                </button>
                              </div>
                            </>
                          )}
                        </div>

                        {/* ── STEP 2: Photos + Video (only shown after start date is locked) ── */}
                        {startDateLocked && (
                          <div style={{
                            padding: "0.85rem", background: "#f0f9ff",
                            borderRadius: "0.5rem", border: "1px solid #bae6fd",
                          }}>
                            <p style={{ fontSize: "0.82rem", fontWeight: 700, marginBottom: "0.75rem", color: "#0369a1" }}>
                              Step 2 — Add 3 site photos and submit
                            </p>

                            {/* 3 photo slots */}
                            <div style={{ display: "flex", gap: "0.5rem", marginBottom: "1rem" }}>
                              <PhotoSlot label="Photo 1" required inputId="photo-1" inputRef={photo1Ref}
                                preview={photo1Preview} onSelect={(f) => { setPhoto1File(f); setPhoto1Preview(URL.createObjectURL(f)); }}
                                onClear={() => { setPhoto1File(null); setPhoto1Preview(null); if (photo1Ref.current) photo1Ref.current.value = ""; }} />
                              <PhotoSlot label="Photo 2" required inputId="photo-2" inputRef={photo2Ref}
                                preview={photo2Preview} onSelect={(f) => { setPhoto2File(f); setPhoto2Preview(URL.createObjectURL(f)); }}
                                onClear={() => { setPhoto2File(null); setPhoto2Preview(null); if (photo2Ref.current) photo2Ref.current.value = ""; }} />
                              <PhotoSlot label="Photo 3" required inputId="photo-3" inputRef={photo3Ref}
                                preview={photo3Preview} onSelect={(f) => { setPhoto3File(f); setPhoto3Preview(URL.createObjectURL(f)); }}
                                onClear={() => { setPhoto3File(null); setPhoto3Preview(null); if (photo3Ref.current) photo3Ref.current.value = ""; }} />
                            </div>

                            {/* Optional video */}
                            <div style={{ marginBottom: "1rem" }}>
                              <p style={{ fontSize: "0.8rem", fontWeight: 600, marginBottom: "0.35rem", color: "#374151" }}>
                                Video <span style={{ color: "#6b7280", fontWeight: 400 }}>(optional · max 25 MB)</span>
                              </p>
                              {videoError && <p style={{ color: "#b91c1c", fontSize: "0.78rem", marginBottom: "0.4rem" }}>{videoError}</p>}
                              {!videoPreviewUrl ? (
                                <div style={{ position: "relative" }}>
                                  <input
                                    type="file" accept="video/*" id="video-capture" ref={videoInputRef}
                                    onChange={(e) => {
                                      setVideoError(null);
                                      const f = e.target.files?.[0];
                                      if (!f) return;
                                      if (f.size > MAX_VIDEO_BYTES) {
                                        setVideoError("Video too large (max 25 MB). Trim and try again.");
                                        if (videoInputRef.current) videoInputRef.current.value = "";
                                        return;
                                      }
                                      setVideoFile(f);
                                      setVideoPreviewUrl(URL.createObjectURL(f));
                                    }}
                                    style={{ opacity: 0, position: "absolute", inset: 0, width: "100%", height: "100%", cursor: "pointer" }}
                                  />
                                  <label htmlFor="video-capture" style={{
                                    display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
                                    border: "2px dashed #d1d5db", borderRadius: "0.5rem", padding: "0.85rem 0.5rem",
                                    cursor: "pointer", background: "#f9fafb", color: "#6b7280", fontSize: "0.8rem",
                                    pointerEvents: "none",
                                  }}>
                                    <span style={{ fontSize: "1.4rem", marginBottom: "0.2rem" }}>🎥</span>
                                    Tap to add video
                                  </label>
                                </div>
                              ) : (
                                <div>
                                  <video src={videoPreviewUrl} controls style={{ width: "100%", borderRadius: "0.375rem", border: "1px solid #d1d5db", maxHeight: "180px" }} />
                                  <button type="button" onClick={() => { setVideoFile(null); setVideoPreviewUrl(null); setVideoError(null); if (videoInputRef.current) videoInputRef.current.value = ""; }}
                                    style={{ marginTop: "0.3rem", width: "100%", fontSize: "0.75rem", padding: "0.3rem", background: "#fee2e2", color: "#b91c1c", border: "none", borderRadius: "0.25rem", cursor: "pointer" }}>
                                    Remove video
                                  </button>
                                </div>
                              )}
                            </div>

                            {/* Submit */}
                            <button
                              type="button"
                              className="btn btn--primary btn--tg"
                              style={{ width: "100%" }}
                              disabled={submitting || !photo1File || !photo2File || !photo3File}
                              onClick={() => void handleSubmitPhase()}
                            >
                              {submitting ? "Uploading…" : "Submit Progress"}
                            </button>
                            {(!photo1File || !photo2File || !photo3File) && (
                              <p style={{ fontSize: "0.76rem", color: "#6b7280", marginTop: "0.4rem", textAlign: "center" }}>
                                Add all 3 photos to enable submit.
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}

            {!selectedBen.activePhaseId && selectedBen.timeline.every((p) => p.status === "Approved") && (
              <div className="alert alert--success" style={{ marginTop: "1rem" }}>
                All phases for this beneficiary have been completed and approved. 🎉
              </div>
            )}
          </div>
        </main>
      </div>
    );
  }

  // ── Beneficiary List View ────────────────────────────────────────────────
  return (
    <div style={{ height: "100vh", overflowY: "auto", backgroundColor: "#f3f4f6" }}>
      <header style={{ backgroundColor: "var(--corp-blue)", color: "white", padding: "1.25rem 1rem", position: "sticky", top: 0, zIndex: 10 }}>
        <h1 style={{ fontSize: "1.25rem", margin: 0, marginBottom: "0.25rem" }}>Subcontractor Portal</h1>
        <p style={{ margin: 0, opacity: 0.9, fontSize: "0.9rem" }}>{data.subcontractorName}</p>
      </header>

      <main style={{ padding: "1rem" }}>
        <h2 className="form-section-title" style={{ marginTop: 0 }}>
          Assigned Beneficiaries ({data.beneficiaries.length})
        </h2>
        {data.beneficiaries.length === 0 ? (
          <div className="card card--flush" style={{ padding: "2rem", textAlign: "center" }}>
            <p className="text-muted">No beneficiaries assigned yet.</p>
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
            {data.beneficiaries.map((ben) => {
              const activePhase = ben.timeline.find((p) => p.id === ben.activePhaseId);
              const activePhaseTitle = activePhase ? activePhase.title : "All Completed / Pending Approval";
              const totalPhases = ben.timeline.length;
              const completedPhases = ben.timeline.filter((p) => p.status === "Approved").length;
              const progressPct = totalPhases > 0 ? Math.round((completedPhases / totalPhases) * 100) : 0;

              return (
                <button
                  key={ben.applicationId}
                  className="card card--elevated"
                  style={{ display: "block", width: "100%", textAlign: "left", padding: "1rem", border: "none", background: "white", cursor: "pointer" }}
                  onClick={() => setSelectedBen(ben)}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "0.5rem" }}>
                    <h3 style={{ margin: 0, fontSize: "1.1rem", color: "#111827" }}>{ben.beneficiaryName}</h3>
                    <span style={{ fontSize: "0.8rem", fontWeight: 600, color: "var(--corp-blue)" }}>{progressPct}%</span>
                  </div>
                  <p style={{ margin: "0 0 0.5rem 0", fontSize: "0.85rem", color: "#4b5563" }}>📍 {ben.village}, {ben.mandal}</p>
                  <div style={{ backgroundColor: "#f3f4f6", padding: "0.5rem", borderRadius: "0.25rem", display: "inline-block", fontSize: "0.8rem", color: "#374151" }}>
                    <strong>Next:</strong> {activePhaseTitle}
                  </div>
                  <div style={{ width: "100%", height: "4px", backgroundColor: "#e5e7eb", borderRadius: "2px", marginTop: "0.75rem", overflow: "hidden" }}>
                    <div style={{ height: "100%", width: `${progressPct}%`, backgroundColor: "var(--corp-blue)", transition: "width 0.3s ease" }} />
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
}
